import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Layout } from '../components/Layout';
import { Upload, Trash2, Image as ImageIcon, Palette } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DesignAdmin() {
  const [patterns, setPatterns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const patternInputRef = useRef(null);
  const templateInputRef = useRef(null);

  const fetchData = () => {
    axios.get(`${API}/api/design/patterns`, { withCredentials: true }).then(r => setPatterns(r.data)).catch(() => {});
    axios.get(`${API}/api/design/templates`, { withCredentials: true }).then(r => setTemplates(r.data)).catch(() => {});
  };

  useEffect(() => { fetchData(); }, []);

  const handleUploadPattern = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/design/admin/patterns`, fd, { withCredentials: true });
      toast.success(`Pattern "${file.name}" ajouté`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
    finally { e.target.value = ''; }
  };

  const handleUploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/design/admin/templates`, fd, { withCredentials: true });
      toast.success(`Template "${file.name}" ajouté`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
    finally { e.target.value = ''; }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Templates & Patterns Maillots</h1>
        </div>

        {/* Patterns */}
        <Card className="border-slate-200" data-testid="admin-patterns-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Palette size={18} className="text-violet-600" /> Patterns ({patterns.length})
              </CardTitle>
              <div>
                <input ref={patternInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleUploadPattern} />
                <Button size="sm" className="gap-1.5 bg-violet-600 hover:bg-violet-700" onClick={() => patternInputRef.current?.click()}>
                  <Upload size={14} /> Ajouter un pattern
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {patterns.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucun pattern</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {patterns.map(p => (
                  <div key={p.id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    {p.thumbnail && <img src={p.thumbnail} alt={p.name} className="w-full h-28 object-cover bg-slate-50" />}
                    <div className="p-2 bg-white">
                      <p className="text-xs font-medium text-slate-700 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400">{p.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Templates */}
        <Card className="border-slate-200" data-testid="admin-templates-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ImageIcon size={18} className="text-blue-600" /> Templates Maillots ({templates.length})
              </CardTitle>
              <div>
                <input ref={templateInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleUploadTemplate} />
                <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => templateInputRef.current?.click()}>
                  <Upload size={14} /> Ajouter un template
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-4">Uploadez vos templates de maillots vierges (PNG/JPG). Le Designer appliquera les couleurs et patterns dessus.</p>
            {templates.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucun template</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {templates.map(t => (
                  <div key={t.id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="h-40 bg-slate-50 flex items-center justify-center p-2">
                      <ImageIcon size={32} className="text-slate-300" />
                    </div>
                    <div className="p-2 bg-white">
                      <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                      <p className="text-[10px] text-slate-400">{t.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="pt-5">
            <h3 className="font-semibold text-amber-900 mb-2">Guide d'utilisation</h3>
            <ul className="text-sm text-amber-800 space-y-1.5 list-disc ml-5">
              <li><strong>Patterns</strong> : Formats PNG avec fond transparent, noir/blanc. Les parties noires deviendront la couleur principale du client, les blanches la couleur secondaire.</li>
              <li><strong>Templates</strong> : Photos de maillots vierges (PNG/JPG). Le pattern colorisé sera appliqué sur la forme du maillot.</li>
              <li><strong>Logos</strong> : Le logo Panther est placé automatiquement en haut à droite. Le logo du client est placé sur le cœur (gauche).</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
